import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Check } from 'lucide-react';
import { shareNews } from '../utils/newbackendcall';
import { useUser } from '../../../../context/UserContext';
import '../styles/BulkShareModal.css';

const BulkShareModal = ({ isOpen, onClose, selectedArticles }) => {
    const { user } = useUser();
    const [senderName, setSenderName] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [recipientsText, setRecipientsText] = useState('');
    const [comments, setComments] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (user) {
            const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
            setSenderName(fullName || '');
            setCompanyName(user.company_name_for_growth_iq || '');
        }
    }, [user]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    /* OLD EMAIL INPUT METHOD - Commented out
    const addRecipient = () => {
        if (recipients.length < 5) {
            setRecipients([...recipients, '']);
        }
    };

    const removeRecipient = (index) => {
        if (recipients.length > 1) {
            setRecipients(recipients.filter((_, i) => i !== index));
        }
    };

    const updateRecipient = (index, value) => {
        const newRecipients = [...recipients];
        newRecipients[index] = value;
        setRecipients(newRecipients);
    };
    */

    const handleSend = async () => {
        // Check for invalid separators (commas, newlines, etc.)
        if (recipientsText.includes(',')) {
            alert('Please use semicolons (;) to separate email addresses, not commas.');
            return;
        }
        if (recipientsText.includes('\n') && recipientsText.trim().split('\n').filter(line => line.trim()).length > 1) {
            alert('Please use semicolons (;) to separate email addresses, not line breaks.');
            return;
        }

        // Parse semicolon-separated emails only
        const emailArray = recipientsText
            .split(';')
            .map(email => email.trim())
            .filter(email => email !== '');

        if (senderName.trim() === '') {
            alert('Please enter your name');
            return;
        }
        if (companyName.trim() === '') {
            alert('Please enter your company name');
            return;
        }
        if (emailArray.length === 0) {
            alert('Please enter at least one recipient email');
            return;
        }
        if (emailArray.length > 5) {
            alert('You can only send to a maximum of 5 recipients. Please remove some emails.');
            return;
        }
        setIsSubmitting(true);
        try {
            const newsItems = selectedArticles.map(article => ({
                news_id: article.id,
                title: article.title,
                url: article.url,
                summary: article.summary || '', // Only included if user has generated a summary
                news_types: article.newsTypes || [],
                published_date: article.publishedDate || '',
                companies: article.companies || [],
                locations: article.locations || [],
                sri_rank_value: article.sriRankValue || 0
            }));
            await shareNews({
                senderName,
                companyName,
                receiverEmails: emailArray,
                newsItems,
                comments: comments.trim() || undefined  // Only include if not empty
            });
            alert('Articles shared successfully!');
            // Don't clear senderName and companyName - they come from user context
            // Only clear the input fields
            setRecipientsText('');
            setComments('');
            onClose();
        } catch (error) {
            console.error('Failed to share articles:', error);
            alert('Failed to share articles. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="bulk-share-modal-overlay-news" onClick={handleOverlayClick}>
            <div className="bulk-share-modal-content-news" onClick={(e) => e.stopPropagation()}>
                <div className="bulk-share-modal-header-news">
                    <h2 className="bulk-share-modal-title-news">
                        Share {selectedArticles.length} selected {selectedArticles.length === 1 ? 'article' : 'articles'}
                    </h2>
                    <button className="bulk-share-modal-close-news" onClick={onClose} aria-label="Close modal">
                        <X size={24} />
                    </button>
                </div>

                <div className="bulk-share-modal-body-news">
                    <div className="selected-articles-section-news">
                        <h3 className="section-title-news">Selected Articles</h3>
                        <ul className="selected-articles-list-news">
                            {selectedArticles.map((article) => (
                                <li key={article.id} className="selected-article-item-news">
                                    <Check size={16} className="check-icon-news" />
                                    <span className="article-title-text-news">{article.title}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="bulk-share-form-group-news">
                        <div className="sender-info-news">
                            <span className="sender-label-news">From:</span>
                            <span className="sender-details-news">{senderName} ({companyName})</span>
                        </div>
                    </div>

                    <div className="bulk-share-form-group-news">
                        <label className="recipients-label-news">Recipients (max 5)</label>
                        <textarea
                            placeholder="email1@example.com; email2@example.com; email3@example.com"
                            value={recipientsText}
                            onChange={(e) => setRecipientsText(e.target.value)}
                            className="bulk-share-textarea-news recipients-textarea-news"
                            rows={3}
                            aria-label="Recipient emails"
                        />
                        <p className="recipients-disclaimer-news">
                            Enter email addresses separated by semicolons only (e.g., email1@example.com; email2@example.com; email3@example.com)
                        </p>
                    </div>

                    {/* OLD EMAIL INPUT METHOD - Commented out
                    <div className="bulk-share-form-group-news">
                        <div className="recipients-header-news">
                            <label className="recipients-label-news">Recipients (max 5)</label>
                            <button
                                className="add-recipient-btn-news"
                                onClick={addRecipient}
                                type="button"
                                disabled={recipients.length >= 5}
                            >
                                <Plus size={18} />
                                <span>Add Recipient</span>
                            </button>
                        </div>

                        {recipients.map((recipient, index) => (
                            <div key={index} className="recipient-input-group-news">
                                <input
                                    type="email"
                                    placeholder="Enter Recipient Email"
                                    value={recipient}
                                    onChange={(e) => updateRecipient(index, e.target.value)}
                                    className="bulk-share-input-news"
                                    aria-label={`Recipient email ${index + 1}`}
                                />
                                {recipients.length > 1 && (
                                    <button
                                        className="remove-recipient-btn-news"
                                        onClick={() => removeRecipient(index)}
                                        type="button"
                                        aria-label={`Remove recipient ${index + 1}`}
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                    */}

                    <div className="bulk-share-form-group-news">
                        <label className="bulk-share-label-news">Add a comment (optional)</label>
                        <textarea
                            placeholder="Write your comments here..."
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            className="bulk-share-textarea-news"
                            rows={4}
                            aria-label="Comments"
                        />
                    </div>
                </div>

                <div className="bulk-share-modal-footer-news">
                    <button
                        className="send-button-news"
                        onClick={handleSend}
                        type="button"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Sending...' : 'Send'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BulkShareModal;